import type {
  ProFeeCodeEvidence,
  ProFeeCodeRow,
  ProFeeCodeSource,
  ProFeeCodingResult,
  ProFeeCodingResultPatient,
  ProFeeCodingResultsResponse,
} from "../types/pro-fee-coding-results";

const NEW_PATIENT_EM_CODES = new Set(["99202", "99203", "99204", "99205"]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const asText = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const asStringList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => (typeof item === "string" ? item.trim() : ""))
    .filter(Boolean);
};

const asSource = (value: unknown): ProFeeCodeSource | undefined => {
  const text = asText(value);
  if (text === "AI" || text === "MANUAL") return text;
  if (value === null) return null;
  return undefined;
};

const emptyResponse = (
  extra?: Partial<ProFeeCodingResultsResponse>
): ProFeeCodingResultsResponse => ({
  success: extra?.success ?? false,
  providerNpi: extra?.providerNpi ?? "",
  count: extra?.count ?? 0,
  results: extra?.results ?? [],
  error: extra?.error,
});

const normalizeEvidence = (value: unknown): ProFeeCodeEvidence | null => {
  if (!isRecord(value)) return null;
  const text = asText(value.text);
  if (!text) return null;
  return {
    text,
    evidenceType: asText(value.evidenceType) || undefined,
    file: asText(value.file) || undefined,
    dos: asText(value.dos) || undefined,
    page: asText(value.page) || undefined,
    source: asSource(value.source),
    selected: value.selected === true,
  };
};

const normalizeCodeRow = (
  value: unknown,
  fallbackIndex: number,
  pdx?: string
): ProFeeCodeRow | null => {
  if (!isRecord(value)) return null;
  const code = asText(value.code);
  if (!code) return null;
  const index =
    typeof value.index === "number" && Number.isFinite(value.index)
      ? value.index
      : fallbackIndex;
  const isPdx =
    value.isPdx === true || (Boolean(pdx) && code === pdx);
  return {
    index,
    code,
    description: asText(value.description),
    type: asText(value.type) || undefined,
    section: asText(value.section) || undefined,
    labels: asStringList(value.labels),
    source: asSource(value.source),
    units: asText(value.units) || null,
    unitsDisplay: asText(value.unitsDisplay) || null,
    modifiers: asStringList(value.modifiers),
    diagnosisMappings: asStringList(value.diagnosisMappings),
    isPdx,
    evidences: Array.isArray(value.evidences)
      ? value.evidences
          .map(normalizeEvidence)
          .filter((item): item is ProFeeCodeEvidence => Boolean(item))
      : [],
  };
};

const normalizePatient = (
  value: unknown
): ProFeeCodingResultPatient | undefined => {
  if (!isRecord(value)) return undefined;
  return {
    patientName: asText(value.patientName) || undefined,
    mrn: asText(value.mrn) || undefined,
    dob: asText(value.dob) || undefined,
    gender: asText(value.gender) || undefined,
    dateOfService: asText(value.dateOfService) || undefined,
    provider: asText(value.provider) || undefined,
  };
};

const normalizeResult = (value: unknown): ProFeeCodingResult | null => {
  if (!isRecord(value)) return null;
  const pdx = asText(value.pdx) || undefined;
  const diagnoses = Array.isArray(value.diagnoses)
    ? value.diagnoses
        .map((row, index) => normalizeCodeRow(row, index + 1, pdx))
        .filter((row): row is ProFeeCodeRow => Boolean(row))
    : [];
  const cptCodes = Array.isArray(value.cptCodes)
    ? value.cptCodes
        .map((row, index) => normalizeCodeRow(row, index + 1, pdx))
        .filter((row): row is ProFeeCodeRow => Boolean(row))
    : [];
  const emCodes = Array.isArray(value.emCodes)
    ? value.emCodes
        .map((row, index) => normalizeCodeRow(row, index + 1, pdx))
        .filter((row): row is ProFeeCodeRow => Boolean(row))
    : [];

  const caseId = asText(value.caseId);
  if (!caseId && !diagnoses.length && !cptCodes.length && !emCodes.length) {
    return null;
  }

  const diagnosisDescriptions: Record<string, string> = {};
  if (isRecord(value.diagnosisDescriptions)) {
    Object.entries(value.diagnosisDescriptions).forEach(([key, description]) => {
      const code = asText(key);
      const text = asText(description);
      if (code && text) diagnosisDescriptions[code] = text;
    });
  }

  return {
    caseId: caseId || "coding-result",
    projectId: asText(value.projectId) || undefined,
    reviewId: asText(value.reviewId) || undefined,
    revisionNo:
      typeof value.revisionNo === "number" ? value.revisionNo : undefined,
    patient: normalizePatient(value.patient),
    isFinalized: value.isFinalized === true,
    finalizedAt:
      asText(value.finalizedAt) || asText(value.finalisedAt) || undefined,
    finalizedBy:
      asText(value.finalizedBy) || asText(value.finalisedByName) || undefined,
    pdx,
    diagnosisDescriptions,
    diagnoses,
    cptCodes,
    emCodes,
    updatedAt: asText(value.updatedAt) || undefined,
  };
};

export const normalizeProFeeCodingResultsResponse = (
  payload: unknown
): ProFeeCodingResultsResponse => {
  if (!isRecord(payload)) {
    return emptyResponse({ error: "Unexpected coding results response" });
  }

  const error = asText(payload.error);
  if (payload.success === false) {
    return emptyResponse({
      success: false,
      providerNpi: asText(payload.providerNpi),
      error: error || "Unable to load coding results",
    });
  }

  const results = Array.isArray(payload.results)
    ? payload.results
        .map(normalizeResult)
        .filter((result): result is ProFeeCodingResult => Boolean(result))
    : [];

  return {
    success: payload.success !== false,
    providerNpi: asText(payload.providerNpi),
    count:
      typeof payload.count === "number" && Number.isFinite(payload.count)
        ? payload.count
        : results.length,
    results,
  };
};

export const currentCodingResult = (
  response?: ProFeeCodingResultsResponse | null
): ProFeeCodingResult | null => response?.results?.[0] ?? null;

export const codingResultHasCodes = (result?: ProFeeCodingResult | null) =>
  Boolean(
    result &&
      (result.diagnoses.length || result.cptCodes.length || result.emCodes.length)
  );

export const splitCodeLabels = (labels: string[]) => ({
  underCode: labels.filter((label) => label === "CC" || label === "MCC"),
  underDescription: labels.filter((label) => label !== "CC" && label !== "MCC"),
});

export const isNewPatientEmCode = (code: string) =>
  NEW_PATIENT_EM_CODES.has(code.trim());

export const procedureUnitsDisplay = (row: ProFeeCodeRow) =>
  row.unitsDisplay?.trim() || "x1";

export const codeRowKey = (prefix: string, row: ProFeeCodeRow) =>
  `${prefix}-${row.index}-${row.code}`;

export const defaultOpenDiagnosisKey = (diagnoses: ProFeeCodeRow[]) => {
  const match = diagnoses.find((row) =>
    row.evidences.some((evidence) => evidence.selected)
  );
  return match ? codeRowKey("diagnosis", match) : null;
};

export const labelChipStyle = (
  label: string
): { background: string; color: string } => {
  const key = label.split("-")[0];
  if (label === "MCC" || key === "MCC") {
    return { background: "#F3AC3F", color: "#000000" };
  }
  if (label === "CC" || key === "CC") {
    return { background: "#f3d540", color: "#000000" };
  }
  if (label === "PDX" || key === "PDX") {
    return { background: "#1d4ed8", color: "#ffffff" };
  }
  if (label === "SDOH" || key === "SDOH") {
    return { background: "#3b82f6", color: "#ffffff" };
  }
  if (label === "ELIX" || key === "ELIX") {
    return { background: "#166534", color: "#ffffff" };
  }
  if (label === "HAC" || key === "HAC") {
    return { background: "#ef4444", color: "#ffffff" };
  }
  if (label.startsWith("HCC") || key === "HCC") {
    return { background: "#ff672e", color: "#ffffff" };
  }
  if (label.startsWith("Rx") || key === "Rx") {
    return { background: "#93c5fd", color: "#000000" };
  }
  return { background: "#e5e7eb", color: "#374151" };
};
